'use client';
import {
  Linkedin,
  Twitter,
  Youtube,
  Mail,
  MapPin,
  Phone,
} from 'lucide-react';
import Link from 'next/link';
import Logo from './Logo';

const footerLinks = {
  product: {
    title: 'Product',
    links: [
      { label: 'Features', href: '#features' },
      { label: 'Industries', href: '#industries' },
      { label: 'How It Works', href: '#how-it-works' },
      { label: 'Pricing', href: '#pricing' },
      { label: 'Security', href: '#security' },
    ],
  },
  resources: {
    title: 'Resources',
    links: [
      { label: 'Blog', href: '#blog' },
      { label: 'Webinars', href: '#webinars' },
      { label: 'Templates', href: '#templates' },
    ],
  },
  company: {
    title: 'Company',
    links: [
      { label: 'About Us', href: '#about' },
      { label: 'Careers', href: '#careers' },
      { label: 'Partners', href: '#partners' },
      { label: 'Contact', href: '#contact' },
      { label: 'Press', href: '#press' },
    ],
  },
  legal: {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '#privacy' },
      { label: 'Terms of Service', href: '#terms' },
      { label: 'Cookie Policy', href: '#cookies' },
      { label: 'GDPR', href: '#gdpr' },
    ],
  },
};

const socialLinks = [
  { icon: Linkedin, href: '#linkedin', label: 'LinkedIn' },
  { icon: Twitter, href: '#twitter', label: 'Twitter' },
  { icon: Youtube, href: '#youtube', label: 'YouTube' },
];

export default function Footer() {
  return (
    <footer className="relative bg-[hsl(var(--slate-deep))] text-white overflow-hidden">
      {/* Subtle gradient top border */}
      <div className="h-px bg-gradient-to-r from-transparent via-[hsl(var(--amber))]/30 to-transparent" />

      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            {/* Logo */}
            <Link href="/" className="inline-block mb-6">
              <Logo size="md" animated={false} />
            </Link>

            <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-sm">
              Transform qualitative risk assessments into precise financial
              figures. Built for operational technology, designed for the
              boardroom.
            </p>

            {/* Contact info */}
            <div className="space-y-3 text-sm text-white/60">
              <a
                href="mailto:hello@impactot.com"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                hello@impactot.com
              </a>
              <a
                href="tel:+442012345678"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4" />
                +44 20 1234 5678
              </a>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>London, United Kingdom</span>
              </div>
            </div>

            {/* Social links */}
            <div className="flex items-center gap-3 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 flex items-center justify-center transition-all duration-200"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5 text-white/60" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([key, section]) => (
            <div key={key}>
              <h4 className="font-medium text-white mb-4">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-sm text-white/60 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter signup */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h4 className="font-medium text-white mb-1">Stay informed</h4>
              <p className="text-sm text-white/60">
                Get monthly insights on BIA best practices and OT security
                trends.
              </p>
            </div>
            <div className="flex gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/40 text-sm w-64 focus:outline-none focus:border-[hsl(var(--amber))]/50 focus:ring-1 focus:ring-[hsl(var(--amber))]/50 transition-all"
              />
              <button className="px-6 py-3 rounded-lg bg-gradient-to-r from-[hsl(var(--amber))] to-[hsl(var(--amber-light))] text-white font-medium text-sm hover:shadow-lg hover:shadow-[hsl(var(--amber))]/20 transition-all hover:-translate-y-0.5">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-sm text-white/40">
              © {new Date().getFullYear()} ImpactOT. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 text-sm text-white/40">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                All systems operational
              </div>
              <a
                href="#status"
                className="text-sm text-white/40 hover:text-white transition-colors"
              >
                System Status
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative background elements */}
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[hsl(var(--amber))] rounded-full opacity-[0.02] blur-3xl -translate-x-1/2 translate-y-1/2" />
      <div className="absolute top-0 right-0 w-60 h-60 bg-[hsl(var(--teal))] rounded-full opacity-[0.02] blur-3xl translate-x-1/2 -translate-y-1/2" />
    </footer>
  );
}
